// Batch loading hook for all menu item options
// This hook fetches all menu item options for a restaurant in a single query
// instead of making N queries (one per menu item when adding to order)

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { MenuItemOption } from '@/types/entities'
import { isAssistantLoggedIn } from '@/lib/permissions'

export const menuItemOptionsKeys = {
  all: ['menu-item-options-all'] as const,
  byRestaurant: (restaurantId: string) => [...menuItemOptionsKeys.all, 'restaurant', restaurantId] as const,
}

/**
 * Fetch all menu item options for a restaurant in a single query
 * Returns a Map<menuItemId, MenuItemOption[]> for O(1) lookups
 *
 * This replaces N individual queries when adding items to an order
 */
export function useAllMenuItemOptions(restaurantId: string | null) {
  return useQuery({
    queryKey: menuItemOptionsKeys.byRestaurant(restaurantId || ''),
    staleTime: 10 * 60 * 1000, // 10 minutes - options don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<Map<string, MenuItemOption[]>> => {
      if (!restaurantId) {
        return new Map()
      }

      // Use RPC function for batch loading - much more efficient than N+1 queries
      const rpcName = isAssistantLoggedIn()
        ? 'assistant_get_all_menu_item_options'
        : 'get_all_menu_item_options'

      // Cast to any since these RPC functions are added via migration and not in generated types yet
      const { data, error } = await (supabase.rpc as any)(rpcName, {
        p_restaurant_id: restaurantId,
      })

      if (error) {
        // Handle permission denied for assistants gracefully
        if (error.message?.includes('PERMISSION_DENIED')) {
          console.warn('Permission denied for menu item options')
          return new Map()
        }
        console.error('Error fetching all menu item options:', error)
        throw error
      }

      // Group by menu_item_id into a Map for O(1) lookups
      const optionsMap = new Map<string, MenuItemOption[]>()

      ;(data || []).forEach((option: any) => {
        const menuItemId = option.menu_item_id
        const mappedOption: MenuItemOption = {
          id: option.id,
          menuItemId: option.menu_item_id,
          nameAr: option.name_ar,
          nameFr: option.name_fr,
          priceModifier: option.price_modifier || 0,
          isDefault: option.is_default || false,
          sortOrder: option.sort_order || 0,
          createdAt: option.created_at || '',
          updatedAt: option.updated_at || '',
        }

        const existing = optionsMap.get(menuItemId) || []
        optionsMap.set(menuItemId, [...existing, mappedOption])
      })

      return optionsMap
    },
    enabled: !!restaurantId,
  })
}

/**
 * Helper function to get options for a specific menu item from the batch-loaded data
 * Use this in components that need to get options for a single menu item
 */
export function getMenuItemOptionsFromBatch(
  optionsMap: Map<string, MenuItemOption[]> | undefined,
  menuItemId: string | null
): MenuItemOption[] {
  if (!optionsMap || !menuItemId) {
    return []
  }
  return optionsMap.get(menuItemId) || []
}
